import React from 'react';

import {
    LIST_MONITORING_HEALTH_COMPONENTS_RESOURCE,
    useListMonitoringHealthComponents,
} from 'src/features/monitoring/api/useListMonitoringHealthComponents';
import {
    LIST_MONITORING_HEALTH_PAY_STATIONS_RESOURCE,
    useListMonitoringHealthPayStations,
} from 'src/features/monitoring/api/useListMonitoringHealthPayStations';
import { SYSTEM_COMPONENT_STATUS } from 'src/features/systems/utils/types';
import WSContext, { WebSocketGroup } from 'src/features/visitor/context/WSContext';
import { WebSocketEvent, WebSocketEventMessages } from 'src/features/visitor/types';
import { queryClient } from 'src/lib/react-query';

type HealthCounts = {
    errors: number;
    warnings: number;
    isLoading: boolean;
};

export function useHealthCounts(parkingId: string, enabled: boolean = true): HealthCounts {
    const { subscribe, unsubscribe } = React.useContext(WSContext);

    const queryConfig = { enabled: enabled && !!parkingId };

    const componentErrors = useListMonitoringHealthComponents({
        parkingId,
        limit: 1,
        status: SYSTEM_COMPONENT_STATUS.ERROR,
        config: queryConfig,
    });
    const componentWarnings = useListMonitoringHealthComponents({
        parkingId,
        limit: 1,
        status: SYSTEM_COMPONENT_STATUS.WARNING,
        config: queryConfig,
    });
    const componentNoConnection = useListMonitoringHealthComponents({
        parkingId,
        limit: 1,
        status: SYSTEM_COMPONENT_STATUS.NO_CONNECTION,
        config: queryConfig,
    });
    const payStationErrors = useListMonitoringHealthPayStations({
        parkingId,
        limit: 1,
        status: SYSTEM_COMPONENT_STATUS.ERROR,
        config: queryConfig,
    });
    const payStationWarnings = useListMonitoringHealthPayStations({
        parkingId,
        limit: 1,
        status: SYSTEM_COMPONENT_STATUS.WARNING,
        config: queryConfig,
    });

    const handleWebSocket = React.useCallback((event: WebSocketEvent) => {
        if (event.message !== WebSocketEventMessages.HEALTH_STATUS_UPDATED) return;
        void queryClient.invalidateQueries({ queryKey: [LIST_MONITORING_HEALTH_COMPONENTS_RESOURCE] });
        void queryClient.invalidateQueries({ queryKey: [LIST_MONITORING_HEALTH_PAY_STATIONS_RESOURCE] });
    }, []);

    React.useEffect(() => {
        if (!enabled) return;
        subscribe(WebSocketGroup.MONITORING_HEALTH, handleWebSocket);
        return () => unsubscribe(WebSocketGroup.MONITORING_HEALTH, handleWebSocket);
    }, [enabled, handleWebSocket, subscribe, unsubscribe]);

    return {
        errors:
            (componentErrors.data?.count ?? 0) +
            (payStationErrors.data?.count ?? 0) +
            (componentNoConnection.data?.count ?? 0),
        warnings: (componentWarnings.data?.count ?? 0) + (payStationWarnings.data?.count ?? 0),
        isLoading:
            componentErrors.isLoading ||
            componentWarnings.isLoading ||
            payStationErrors.isLoading ||
            payStationWarnings.isLoading,
    };
}
